import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { openModalState } from 'recoils/atoms'

import { Button, Modal, PromptGuard } from 'components'

import {
  initialFormPendingTicket,
  schemaFormPendingTicket,
} from 'features/Ticket'

import { usePromptForm } from 'helpers/hooks'

import TicketPendingModalForm from './TicketPendingModalForm'

import { isEmpty } from 'lodash'

import type { ISchemaFormPendingTicket } from '../model'

export interface IPendingModalProps {
  isOpen: boolean
  onClose: () => void
  setDisabledPendingButton: (loadSubmit: boolean) => void
  setConfirmModal: () => void
  payloadBeforeSubmit?: ISchemaFormPendingTicket
  setPayloadBeforeSubmit: (payload: ISchemaFormPendingTicket) => void
  setOpenPendingTicket: () => void
}

function TicketPendingModal(props: IPendingModalProps): JSX.Element {
  const { t } = useTranslation(['ticket', 'common'])
  const setIsOpenModal = useSetRecoilState(openModalState)

  const {
    isOpen,
    onClose,
    setConfirmModal,
    payloadBeforeSubmit,
    setPayloadBeforeSubmit,
    setOpenPendingTicket,
  } = props

  const base = 'f-ticket-pending-modal'

  const methods = useForm({
    defaultValues: initialFormPendingTicket,
    resolver: yupResolver(schemaFormPendingTicket),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  useEffect(() => {
    if (shouldPrompt || methods.formState.isSubmitted) {
      return
    }

    if (methods.formState.isDirty) {
      setShouldPrompt(true)
    }
  }, [methods.formState])

  useEffect(() => {
    methods.setValue('pendingReason', payloadBeforeSubmit?.pendingReason, {
      shouldDirty: true,
    })
    methods.setValue('pendingDetail', payloadBeforeSubmit?.pendingDetail, {
      shouldDirty: true,
    })
  }, [])

  const [shouldPrompt, setShouldPrompt] = usePromptForm(methods)

  const beforeSubmit = async (value: ISchemaFormPendingTicket) => {
    if (isEmpty(methods.errors)) {
      setConfirmModal()
      setPayloadBeforeSubmit(value)

      await setTimeout(() => {
        setShouldPrompt(false)
      }, 2000)
    }
  }

  const handleCloseModal = () => {
    setIsOpenModal(false)

    if (shouldPrompt) {
      onClose()
    } else {
      setOpenPendingTicket()
      setPayloadBeforeSubmit(initialFormPendingTicket)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      isPending={true}
      isIcon={false}
      isFilledModal
    >
      <div className={`${base}__content`}>
        <div className={`${base}__modal-header`}>
          <span className={`${base}__modal-header--title`}>
            {t('ticket:requestTaskPendingReason')}
          </span>
        </div>

        <FormProvider {...methods}>
          <PromptGuard when={shouldPrompt} />

          <form
            className={`${base}__form`}
            onSubmit={methods.handleSubmit(beforeSubmit)}
          >
            <TicketPendingModalForm
              payloadBeforeSubmit={payloadBeforeSubmit}
              setPayloadBeforeSubmit={setPayloadBeforeSubmit}
            />
            <div className={`${base}__button`}>
              <Button
                className={`${base}__button--action-button`}
                variants='secondary'
                onClick={handleCloseModal}
              >
                {t('common:cancel.button')}
              </Button>
              <Button
                className={`${base}__button--action-button`}
                type='submit'
              >
                {t('common:submit.button')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}

export default TicketPendingModal
